/**
 * Shared zod schemas + types for the VK Teams bot surface (M2 Phase 7).
 *
 * Covers:
 *   - VK Teams provider settings (`BotSettingsSchema`)
 *   - User-facing notification preferences (`NotificationPrefsSchema`)
 *   - Profile fragment for an existing binding (`VkBindingFragmentSchema`)
 *   - FSM stage enum for `BotConversationState.stage`
 *
 * R2 / QA NIT-1: `BotConversationStage` intentionally encodes only the
 * 6 active stages. The IDLE state is REPRESENTED BY ROW-ABSENCE — a
 * conversation row simply doesn't exist when the bot has no in-flight
 * dialog with a given (vkUserId, vkChatId). Encoding IDLE as an enum
 * value would invite zombie rows that the cleanup job can't tell apart
 * from a real conversation that's been stuck for ages.
 */
import { z } from 'zod';
import { AccessLevel } from './enums';

// ─── Bot role gate (M2 P7 W3.3) ────────────────────────────────────────────

/**
 * Predicate: is this user allowed to bind the VK Teams bot to their account?
 *
 * Design intent (user 2026-05-19): admins (incl. ГИП — ADMIN tier per the
 * DB trigger in migration 003), руководители отделов (MANAGER), and
 * системные администраторы (SUPERVISOR / SYSADMIN) do not submit reports
 * and do not have a fact-submission plan, so they have no reason to use
 * the bot. Restricting bot bind to executors (EMPLOYEE tier) keeps the
 * surface tight and avoids confusing UX for managers who would never
 * receive a reminder anyway.
 *
 * Used in three places that MUST agree to avoid silent role-escalation:
 *   1. `POST /users/me/vk-binding/code` controller-side gate (HTTP 403)
 *   2. `BindFlow` consume step — rejects the /bind <code> action if the
 *      code was issued for a non-EMPLOYEE user (defense in depth in case
 *      a code outlives a role demotion)
 *   3. FE «VK Teams» card on Profile → Уведомления — disables the
 *      «Привязать» button with explanatory copy.
 *
 * The function takes the raw enum value (not the role slug) so it survives
 * future role taxonomy refactors as long as `ROLE_ACCESS_LEVEL` keeps
 * mapping every slug to one of the four tiers.
 */
export function canBindBot(accessLevel: AccessLevel): boolean {
  // Блок 2 · Ф5 · В9-a — расширено до MANAGER и ADMIN.
  //
  // Основание — решение 15 «Бота разрешаем руководителям» и Р-69
  // («руководитель получает в боте три действия»). ⚠️ Р-14 унесла два довода
  // из трёх (транзита и куратора больше нет); решение держится на ТРЕТЬЕМ —
  // уведомление о назначении. Записано затем, чтобы через месяц решение не
  // сняли вместе с исчезнувшими доводами.
  //
  // ⚠️ SUPERVISOR НЕ ВХОДИТ, и это не забывчивость: решение называет
  // MANAGER и ADMIN поимённо. Системный администратор — учётная запись
  // обслуживания, работ на нём нет, уведомлять его не о чем.
  //
  // ⚠️ ЧТО ЭТО ОТКРЫВАЕТ САМО СОБОЙ, БЕЗ ЕДИНОЙ СТРОКИ КОДА: путь к подаче
  // часов из бота. Он закрыт ОТДЕЛЬНО — `canReportFactViaBot` ниже плюс
  // рубеж в сервисе (Р-68). Одного этого предиката для запрета мало:
  // клавиатуру обходит синтетический callback.
  return (
    accessLevel === AccessLevel.EMPLOYEE ||
    accessLevel === AccessLevel.MANAGER ||
    accessLevel === AccessLevel.ADMIN
  );
}

/**
 * Предикат: может ли этот человек подавать факт (часы) ЧЕРЕЗ БОТА.
 *
 * Блок 2 · Ф5 · В9-a, основание — **Р-68 «Руководитель через бота о часах
 * не отчитывается»** (вариант (а) вопроса 1).
 *
 * ⚠️ ЭТО НОВЫЙ РУБЕЖ, А НЕ СОХРАНЕНИЕ СТАТУС-КВО. До В9-a руководитель бота
 * привязать не мог вовсе, и вопрос не вставал; расширение `canBindBot`
 * открыло бы отчёт САМО, без единой строки кода. Молчание здесь тоже было бы
 * решением — только непроизнесённым.
 *
 * ⚠️ ЦЕНА НАЗВАНА ВЛАДЕЛЬЦУ И ПРИНЯТА: сотрудник, который уже привязал бота
 * и отчитывается из чата, СТАВ РУКОВОДИТЕЛЕМ, эту возможность теряет — и
 * теряет молча. Отдельного уведомления решение не предусматривает.
 *
 * ⚠️ РУБЕЖ СТОИТ В СЕРВИСЕ (`createFactFromBot`), А НЕ В КЛАВИАТУРЕ.
 * Скрытая кнопка — не рубеж: синтетический callback обходит клавиатуру
 * целиком. Клавиатура лишь не показывает недоступное.
 *
 * ⚠️ Р-68, ГРАНИЦА 3: решение снимает КАНАЛ БОТА, а не право отчитаться. В
 * вебе руководитель списывает часы как прежде — читать этот предикат как
 * «руководитель не отчитывается о часах» ошибка ровно на слово «через бота».
 */
export function canReportFactViaBot(accessLevel: AccessLevel): boolean {
  return accessLevel === AccessLevel.EMPLOYEE;
}

/** Сообщение человеку при отказе в подаче факта из бота (Р-68). */
export const BOT_FACT_NOT_ALLOWED_MESSAGE_RU =
  'Отчёт о часах через бота доступен только сотрудникам-исполнителям. Списать часы можно в веб-интерфейсе.';

/** Код конверта отказа — стабильная часть контракта. */
export const BOT_FACT_NOT_ALLOWED_FOR_ROLE = 'BOT_FACT_NOT_ALLOWED_FOR_ROLE';

/**
 * Friendly Russian message shown to the user at all three rejection
 * points (REST 403, bot reply, FE helper text). Single source of truth so
 * QA can grep one string. Per UX feedback: «Бот доступен только
 * сотрудникам-исполнителям. Если вы считаете это ошибкой, обратитесь к
 * руководителю.»
 */
export const BOT_BIND_NOT_ALLOWED_MESSAGE_RU =
  'Бот доступен только сотрудникам-исполнителям. Если вы считаете это ошибкой, обратитесь к руководителю.';

/**
 * Structured error envelope code for the REST surface. Mirrors the
 * codebase convention (UPPER_SNAKE for stable contract codes — see
 * `LAST_ADMIN`, `BOT_DISABLED`, etc).
 */
export const BOT_BIND_NOT_ALLOWED_FOR_ROLE = 'BOT_BIND_NOT_ALLOWED_FOR_ROLE';

// ─── VK Teams provider config ──────────────────────────────────────────────

export const BotSettingsSchema = z
  .object({
    enabled: z.coerce.boolean().default(false),
    botToken: z.string().max(512).default(''),
    apiBase: z.string().trim().max(255).default('https://api.internal.myteam.mail.ru/bot/v1'),
  })
  .strict();
export type BotSettings = z.infer<typeof BotSettingsSchema>;

// ─── User notification preferences ─────────────────────────────────────────

/**
 * HH:MM 24-hour clock. Mirrors the Postgres CHECK constraint
 * `user_reminder_time_format` in migration 026 so client + server reject
 * the same set of inputs.
 *
 * W2.2 N1 (DA-N1): values in `[23:55, 23:59]` are FORBIDDEN to defend
 * against the Postgres `time + interval` midnight-wrap. The 5-minute
 * catch-up window in `reminder-query.sql.ts` is computed as
 * `reminderTime::time + interval '5 minutes'`, which wraps modulo 24h
 * (`'23:58'::time + '5 min' = '00:03'`). The downstream `<` comparison
 * then deterministically misses any user with `reminderTime ≥ '23:55'`.
 * Enforcement here mirrors a DB CHECK in migration 029 — defense in depth.
 */
export const ReminderTimeRegex = /^([01]\d|2[0-3]):[0-5]\d$/;
export const ReminderTimePreMidnightMax = '23:54';

export const NotificationPrefsSchema = z
  .object({
    remindersEnabled: z.boolean().default(true),
    reminderTime: z
      .string()
      .regex(ReminderTimeRegex)
      .refine((v) => v <= ReminderTimePreMidnightMax, {
        message: `Время напоминания должно быть не позже ${ReminderTimePreMidnightMax}`,
      })
      .default('18:00'),
    /**
     * ISO-8601 weekday (1=Mon … 7=Sun). Empty array is allowed and means
     * «никогда» — reminders effectively off even when `remindersEnabled`
     * is true. Max 7 elements; uniqueness is NOT enforced at this layer
     * (the API normalises). Matches the `user_reminder_dow_range` CHECK
     * constraint.
     */
    reminderDaysOfWeek: z.array(z.number().int().min(1).max(7)).max(7).default([1, 2, 3, 4, 5]),
  })
  .strict();
export type NotificationPrefs = z.infer<typeof NotificationPrefsSchema>;

// ─── VK Teams binding (profile fragment) ───────────────────────────────────

/**
 * Каналы бота — Ф5 · В8-b.1.
 *
 * ⚠️ ЭТО ТРЕТЬЕ ОБЪЯВЛЕНИЕ ТОГО ЖЕ ПЕРЕЧНЯ, И ДВА ДРУГИХ НАЗВАНЫ ЗДЕСЬ, ЧТОБЫ
 * РАСХОЖДЕНИЕ НЕ ПРОШЛО МОЛЧА. Первое — тип `BotChannel` в Postgres
 * (`CREATE TYPE … AS ENUM ('VK_TEAMS', 'TELEGRAM')`, миграция 057); второе —
 * `BOT_CHANNELS` в `apps/api/src/bot-core/channel.types.ts`, где совпадение с
 * базой проверяется КОМПИЛЯТОРОМ двусторонним присваиванием. Там же добавлена
 * такая же двусторонняя проверка против ЭТОГО объявления — то есть все три
 * связаны попарно, и добавление канала мимо любого из них красит `tsc`.
 *
 * ⚠️ ПОЧЕМУ НЕ ОДНО ОБЪЯВЛЕНИЕ НА ВСЕХ. Ядро бота сознательно не тянет за
 * собой ORM (см. шапку `channel.types.ts`), а `packages/shared` не вправе
 * зависеть от `@prisma/client` вовсе — его собирает и веб. Сведение к одному
 * источнику упирается не в лень, а в направление зависимостей; проверка
 * компилятором дешевле и надёжнее договорённости «не забыть».
 *
 * ⚠️ ПОРЯДОК ЗНАЧИМ: он совпадает с порядком объявления типа в Postgres, и на
 * него опирается последний ключ выбора привязки (`pickDeliveryBinding`).
 * Перечисления в Postgres сортируются порядком объявления, а не алфавитом.
 */
export const BOT_BINDING_CHANNELS = ['VK_TEAMS', 'TELEGRAM'] as const;
export const BotBindingChannelSchema = z.enum(BOT_BINDING_CHANNELS);
export type BotBindingChannel = z.infer<typeof BotBindingChannelSchema>;

/** Человеческое имя канала — для заголовка карточки привязки в профиле. */
export const BOT_CHANNEL_LABEL_RU: Record<BotBindingChannel, string> = {
  VK_TEAMS: 'VK Teams',
  TELEGRAM: 'Telegram',
};

export const VkBindingFragmentSchema = z
  .object({
    vkUserId: z.string(),
    /**
     * Канал привязки — Ф5 · В8-b.1, третье исключение к **Р-63** (ответ
     * владельца «А», 25.08.2026).
     *
     * ⚠️ ПОЛЕ НЕОБЯЗАТЕЛЬНОЕ, И ЭТО НЕ ПОЛУМЕРА, А ТРЕБОВАНИЕ РАСКАТКИ. Веб
     * читает `/auth/me` без разбора схемой (`apiFetch<MeUser>` — голый каст),
     * но бандл, собранный ДО этой волны, поля не знает вовсе, а API, поднятый
     * до веба, его уже отдаёт. `undefined` обязан рендериться ровно как
     * прежняя карточка VK Teams — тогда обе половины выката совместимы в
     * любом порядке.
     *
     * ⚠️ ЧТО ЭТО ПОЛЕ ЗНАЧИТ И ЧЕГО НЕ ЗНАЧИТ: это канал ТОЙ привязки, в
     * которую бот сегодня доставляет (правило выбора — `pickDeliveryBinding`
     * на стороне API), а не перечень каналов человека. Держать привязки в
     * обоих мессенджерах разом он вправе (решение 17); карточка показывает
     * один — тот, куда придут сообщения.
     *
     * ⚠️ ИМЯ `vkUserId` СОСЕДНЕГО ПОЛЯ НЕ ПЕРЕИМЕНОВАНО НАМЕРЕННО.
     * Переименование утащило бы за собой типы бота (`bot-vkteams/types.ts`),
     * форму аудит-снимка (`entity: 'vk_teams_binding'`, ключ `vkUserId`) и
     * три файла веба — это отдельная работа с отдельной оценкой, и третье
     * исключение Р-63 её не разрешает.
     */
    channel: BotBindingChannelSchema.optional(),
    boundAt: z.string(),
    lastUsedAt: z.string().nullable(),
  })
  .strict()
  .nullable();
export type VkBindingFragment = z.infer<typeof VkBindingFragmentSchema>;

// ─── FSM stage enum (BotConversationState.stage values) ────────────────────

/**
 * Active conversation stages. IDLE is row-absence (see file header).
 * The order here mirrors the typical dialog flow; reorderings are
 * SEMANTICALLY irrelevant (DB stores the string verbatim) but kept for
 * readability.
 */
export const BotConversationStage = z.enum([
  'AWAITING_PROJECT',
  'AWAITING_FACTS',
  'AWAITING_CONFIRM',
  'AWAITING_EDIT_CHOICE',
  'AWAITING_EDITED_FIELD',
  'AWAITING_PLAN_DATE',
]);
export type BotConversationStage = z.infer<typeof BotConversationStage>;
